import { useQuery, useQueryClient } from '@tanstack/react-query';
import dataService from 'services/dataService';
import QueryParams from 'utils/queryParams';
import { API_UNTRACKED_CALLS } from 'services/dataService/resources/callManagement/constants';
import { makeid } from 'utils/helpers/makeid';

import { useUntrackedInboundCallsFilters } from './useFilters';

import type { Filters, UntrackedInboundCallsData } from './types';

const queryKeyBase = ['untrackedInboundCalls'] as const;

export const useUntrackedInboundCalls = () => {
  const { filter } = useUntrackedInboundCallsFilters();

  const queryKey = [...queryKeyBase, filter] as const;

  const queryClient = useQueryClient();
  const { data, error, isLoading, isPreviousData, isRefetching } = useQuery({
    queryKey,
    keepPreviousData: true,
    queryFn: () => getItems(filter),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: queryKeyBase });
  };

  return {
    data: {
      data: data?.items,
      count: data?.count,
    },
    invalidate,
    isLoading: isLoading || isPreviousData || isRefetching,
    error,
  };
};

const getItems = async (filters: Filters) => {
  const url = QueryParams.stringifyUrl(API_UNTRACKED_CALLS, filters);
  const { error, data } = await dataService.getList<UntrackedInboundCallsData>(
    url,
  );
  if (error) {
    throw new Error(error);
  }
  if (!data) {
    return undefined;
  }
  return {
    count: data.count,
    items: data.items.map((item) => ({
      ...item,
      id: makeid(10),
    })),
  };
};
